import { CheckboxInputField, FileUploadComponent, SelectInputField } from "@/components/shared";
import { CircleLoader } from "@/components/shared/loaders";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { SelectItem } from "@/components/ui/select";
import { SUPPORTED_FILE_TYPES_STRING } from "@/constants/documents";
import { DocumentType } from "@/graphql/__generated__/graphql";
import { useAddDocumentToTenderMutationHook } from "@/hooks";
import { useTenderPermissionsData } from "@/hooks/cache";
import { requiredFile } from "@/utils/form";
import { useMemo, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { TFunction } from "i18next";
import { Info } from "lucide-react";
import { Dispatch, SetStateAction, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const createAddDocumentSchema = (t: TFunction) => {
  return z.object({
    document: requiredFile(),
    documentType: z.enum([DocumentType.Contract, DocumentType.Publication, DocumentType.ServiceDescription, DocumentType.PriceForm, DocumentType.Other], {
      error: (issue) => issue.input === undefined ?
        t("schemas:form.required")
        :
        t("schemas:form.invalid")
    }),
    addToKnowledgeBase: z.boolean(),
  }).refine(
    (data) => {
      // If documentType is PriceForm, the file must be xlsx
      if (data.documentType === DocumentType.PriceForm && data.document && data.document.length > 0) {
        const file = data.document[0];
        return file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      }
      return true;
    },
    {
      message: t("schemas:createtender.document.priceforminvalidfile"),
      path: ["document"],
    }
  );
};

const addDocumentDefaultValues = {
  document: undefined,
  documentType: undefined,
  addToKnowledgeBase: true,
};

function AddDocumentDialog({ tenderId, setMutationPollingActive }: { tenderId: number, setMutationPollingActive: Dispatch<SetStateAction<boolean>> }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { canManageDocument } = useTenderPermissionsData(Number(tenderId));
  const { AddDocumentToTender, loading } = useAddDocumentToTenderMutationHook(setMutationPollingActive);

  const FormSchema = useMemo(() => createAddDocumentSchema(t), [t]);

  const onInvalid = (errors: unknown) => console.error(errors);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: addDocumentDefaultValues,
    mode: "onChange"
  });

  // Watch documentType to update description dynamically
  const documentType = form.watch("documentType");

  // Get dynamic description based on documentType
  const fileUploadDescription = documentType === DocumentType.PriceForm
    ? t("edittender.documents.adddocument.inputs.fileupload.descriptionpriceform")
    : t("edittender.documents.adddocument.inputs.fileupload.description", { fileTypes: SUPPORTED_FILE_TYPES_STRING });

  // Get accept prop based on documentType - only restrict to XLSX for PriceForm
  const acceptProp = documentType === DocumentType.PriceForm
    ? {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    }
    : undefined;

  // Trigger validation when documentType changes to ensure file type validation runs
  useEffect(() => {
    if (documentType) {
      form.trigger("document");
    }
  }, [documentType, form]);

  const onSubmit = (data: z.infer<typeof FormSchema>) => {
    AddDocumentToTender({
      variables: {
        tenderId,
        document: data.document[0],
        documentType: data.documentType,
        addToKnowledgeBase: data.addToKnowledgeBase
      },
      context: {
        addToKnowledgeBase: data.addToKnowledgeBase
      }
    }).then(() => {
      setOpen(false);
    });
  };

  if (!canManageDocument) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          onClick={() => form.reset(addDocumentDefaultValues)}
        >
          {t("edittender.documents.adddocument.title")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[90vw] lg:max-w-fit overflow-y-auto max-h-[90vh] h-full md:h-auto">
        <Form {...form}>
          <form className="space-y-6" id="ai-import-form" onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
            <DialogHeader>
              <DialogTitle>{t("edittender.documents.adddocument.title")}</DialogTitle>
              <DialogDescription>{t("edittender.documents.adddocument.description")}</DialogDescription>
            </DialogHeader>
            <div className="flex-col-4">
              <FileUploadComponent
                name="document"
                label={t("edittender.documents.adddocument.inputs.fileupload.label")}
                useDefaultDndDescription={true}
                description={fileUploadDescription}
                form={form}
                allowMultiple={false}
                accept={acceptProp}
              />

              <SelectInputField
                form={form}
                name="documentType"
                label={t("edittender.documents.adddocument.inputs.documenttype.label")}
                className="w-full"
              >
                {Object.values(DocumentType).map((type) => (
                  <SelectItem key={type} value={type}>
                    {t("common:documenttype." + type.toLowerCase())}
                  </SelectItem>
                ))}
              </SelectInputField>

              <CheckboxInputField
                form={form}
                name="addToKnowledgeBase"
                label={t("edittender.documents.adddocument.inputs.knowledgebase.label")}
              />
              <Alert>
                <Info />
                <AlertDescription>
                  {t("edittender.documents.adddocument.inputs.knowledgebase.note")}
                </AlertDescription>
              </Alert>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={loading}>{t("common:buttons.close")}</Button>
              </DialogClose>
              <Button type="submit" className="gap-2" disabled={loading}>
                <CircleLoader visible={loading} height={16} width={16} strokeWidth={2} />
                {t("common:buttons.add")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default AddDocumentDialog;
